<template>
    <div>
        <div class="muven-container">
            <div class="muven-router-name-container">
                <p class="muven-router-name">Autenticação</p>
            </div>

            <div class="muven-content">
                <Loading page v-if="loading" />

                <Breadcrumb :items="breadcrumbList" class="mb-3" v-if="!loading" />

                <ResourceNotFound
                    v-if="!loading && loadError"
                    resource="canal"
                    :identifier="$route.params.id"
                    list-path="/canais"
                    list-label="Voltar para canais"
                    :kind="loadErrorKind"
                    :error-message="loadErrorMessage"
                    @retry="load" />

                <div class="channel-auth" v-if="!loading && !loadError">
                    <ChannelPageHeaderCmp :channel="channel" eyebrow="Autenticação">
                        <span class="ca-status" :class="`is-${connectionState}`">
                            <span class="ca-status-dot"></span>
                            {{connectionStateLabel}}
                        </span>
                    </ChannelPageHeaderCmp>

                    <section class="ca-panel">
                        <ChannelConnectionDiagramCmp :channel="channel" :state="connectionState"
                            :failed="connectionState === 'connected' && !!lastTest && !lastTest.ok" />

                        <!-- Não conectado: passo a passo -->
                        <template v-if="connectionState !== 'connected'">
                            <h2 class="ca-title">Conecte sua conta {{channel.getRealName()}}</h2>
                            <p class="ca-lead">Leva menos de um minuto. Você autoriza o Muven direto no {{channel.getRealName()}}, sem digitar chaves.</p>

                            <p class="ca-note is-warning" v-if="channel.isShopify()">
                                <fa icon="headset" class="ca-note-icon" />
                                <span>A integração com a Shopify deve ser feita <strong>junto com o suporte especializado</strong> do Muven para ser configurada e concluída com sucesso.</span>
                            </p>

                            <ol class="ca-steps">
                                <li class="ca-step" v-for="(step, index) in steps" :key="index"
                                    :class="{ 'is-current': index === currentStep, 'is-done': index < currentStep }">
                                    <span class="ca-step-marker">
                                        <fa icon="check" v-if="index < currentStep" />
                                        <template v-else>{{index + 1}}</template>
                                    </span>
                                    <span class="ca-step-text">
                                        <strong>{{step.title}}</strong>
                                        <span>{{step.description}}</span>
                                    </span>
                                </li>
                            </ol>

                            <div class="ca-cta">
                                <button type="button" class="btn btn-primary ca-button" :disabled="authenticating" @click="authenticate()">
                                    <fa icon="sync-alt" spin v-if="authenticating" />
                                    <fa icon="link" v-else />
                                    {{authenticating ? 'Aguardando autorização' : `Conectar ao ${channel.getRealName()}`}}
                                </button>
                                <p class="ca-hint">Se a janela não abrir, libere pop-ups para o Muven no navegador.</p>
                            </div>
                        </template>

                        <!-- Conectado -->
                        <template v-else>
                            <h2 class="ca-title">Conta conectada</h2>
                            <p class="ca-lead">Sua conta {{channel.getRealName()}} está conectada ao Muven.</p>

                            <div class="ca-health">
                                <div class="ca-health-text">
                                    <strong>Saúde da conexão</strong>
                                    <span class="ca-test-result is-ok" v-if="lastTest && lastTest.ok">
                                        <fa icon="check-circle" />
                                        Funcionando - testada às {{lastTest.at}}
                                    </span>
                                    <span class="ca-test-result is-error" v-else-if="lastTest">
                                        <fa icon="exclamation-triangle" />
                                        <span>
                                            Falhou às {{lastTest.at}}: {{lastTest.message}}
                                            <small>Se continuar falhando, desconecte e conecte de novo.</small>
                                        </span>
                                    </span>
                                    <span class="ca-test-result" v-else>Teste para confirmar que o Muven consegue falar com o canal.</span>
                                </div>

                                <button type="button" class="btn btn-outline-secondary ca-button" :disabled="testingConnection" @click="testConnection()">
                                    <fa icon="sync-alt" :spin="testingConnection" />
                                    {{testingConnection ? 'Testando' : 'Testar conexão'}}
                                </button>
                            </div>

                            <div class="ca-danger">
                                <div class="ca-danger-text">
                                    <strong>Desconectar conta</strong>
                                    <span>O Muven para de enviar e receber pedidos, estoque e preços deste canal até você conectar de novo.</span>
                                </div>

                                <button type="button" class="btn btn-outline-danger ca-button" :disabled="removingLink" @click="askRemoveLink()">
                                    <fa icon="sync-alt" spin v-if="removingLink" />
                                    <fa icon="exclamation-triangle" v-else />
                                    Desconectar
                                </button>
                            </div>
                        </template>
                    </section>

                    <div class="ca-footer">
                        <button type="button" class="btn btn-primary ca-button" @click="backToSettingsList()">
                            <fa icon="arrow-left" />
                            Voltar
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal" tabindex="-1" role="dialog" id="shopify-shop-modal">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Integração com Shopify</h5>
                    </div>
                    <div class="modal-body">
                        <p>Informe o nome da sua loja Shopify (sem <code>.myshopify.com</code>):</p>
                        </br>
                        <input type="text" class="form-control" v-model="shopifyShopName" placeholder="ex: minha-loja"
                            @keyup.enter="confirmShopifyShop()" />
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-primary" @click="confirmShopifyShop()" :disabled="!shopifyShopName">
                            Continuar
                        </button>
                        <button class="btn btn-outline-secondary" @click="closeShopifyModal()">
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Fora de .channel-auth: a animação de entrada dela cria um contexto de
             empilhamento e o modal ficaria embaixo do backdrop do Bootstrap. -->
        <ConfirmByTypingModalCmp
            ref="confirmRemoveLink"
            :modal-id="ID_CONFIRM_REMOVE_LINK_MODAL"
            :title="`Desconectar ${channel.getRealName()}`"
            confirmation-word="DESCONECTAR"
            confirm-label="Desconectar"
            confirm-hint="Você precisa confirmar a ação para desvincular o canal."
            @confirm="confirmRemoveLink()">
            <p>
                A conta <strong>{{channel.getRealName()}}</strong> deixa de estar autorizada no Muven.
                Pedidos, estoque e preços param de sincronizar com este canal até você conectar de novo.
            </p>
        </ConfirmByTypingModalCmp>
    </div>
</template>

<script>
import { ChannelModel } from '../models/ChannelModel'
import channelService from '../services/ChannelService'
import subscriberManager from '@/subscriber/managers/SubscriberManager'
import ModalService from '@/utils/services/ModalService'
import ChannelPageHeaderCmp from '@/channel/components/ChannelPageHeaderCmp.vue'
import ChannelConnectionDiagramCmp from '@/channel/components/ChannelConnectionDiagramCmp.vue'
import BrandTileCmp from '@/utils/components/BrandTileCmp.vue'
import ConfirmByTypingModalCmp from '@/utils/components/ConfirmByTypingModalCmp'
import { getChannelBrand } from '@/channel/models/ChannelBrand'
import {
    CredentialsMercadoLivre,
    CredentialsMagazineLuiza,
    CredentialsNuvemShop,
    CredentialsShopee,
    CredentialsWbuy,
    CredentialsShein,
    CredentialsShopify,
    CredentialsTiktokShop
} from '../models/CredentialsModels'
import ResourceLoadError from '../../utils/mixins/ResourceLoadError'
import { getChannelNotFoundBreadcrumb } from '../utils/ChannelNotFoundBreadcrumb'

const ID_CONFIRM_REMOVE_LINK_MODAL = 'muven-confirm-remove-link-modal'

function getCredentialsManager(channel, credentials) {
    if (!channel) {
        return new CredentialsMercadoLivre()
    }

    if (channel.isMercadoLivre()) {
        return new CredentialsMercadoLivre(credentials)
    }

    if (channel.isShopee()) {
        return new CredentialsShopee(credentials)
    }

    if (channel.isMagazineLuiza()) {
        return new CredentialsMagazineLuiza(credentials)
    }

    if (channel.isWBuy()) {
        return new CredentialsWbuy(credentials);
    }

    if (channel.isShein()) {
        return new CredentialsShein(credentials);
    }

    if (channel.isTiktokShop()) {
        return new CredentialsTiktokShop(credentials);
    }

    if (channel.isShopify()) {
        return new CredentialsShopify(credentials)
    }

    return new CredentialsNuvemShop(credentials)
}

export default {
    name: 'ChannelAuthenticationStepByStepView',
    components: {
        ChannelPageHeaderCmp,
        ChannelConnectionDiagramCmp,
        BrandTileCmp,
        ConfirmByTypingModalCmp,
    },
    data() {
        return {
            loading: true,
            authenticating: false,
            removingLink: false,
            testingConnection: false,
            credentials: getCredentialsManager(),
            channel: new ChannelModel(),
            breadcrumbList: [],
            shopifyShopName: '',
            // Resultado do último "Testar conexão", exibido na própria tela
            lastTest: null,
            ID_CONFIRM_REMOVE_LINK_MODAL,
        }
    },
    mixins: [
        ResourceLoadError,
    ],
    mounted() {
        this.load()
    },
    // beforeDestroy() {},
    methods: {
        async load() {
            this.loading = true
            this.loadError = null

            try {
                this.channel = await channelService.getChannelById(this.$route.params.id)
                const credentials = await channelService.getCredentials(this.channel.id)
                this.credentials = getCredentialsManager(this.channel, credentials)

                this.breadcrumbList = [
                    {
                        id: 0,
                        description: 'Canais',
                        path: '/canais'
                    },
                    {
                        id: 1,
                        description: `Configurações ${this.channel.name}`,
                        channel: this.channel,
                        path: `/canais/${this.channel.id}/configuracoes`,
                    },
                    {
                        id: 2,
                        description: `Autenticação`,
                        path: '',
                    },
                ]
            } catch(err) {
                this.loadError = err
                this.breadcrumbList = getChannelNotFoundBreadcrumb(this.$route.params.id)
            }

            this.loading = false
        },

        async authenticate() {
            if (this.channel.isShein()) {
                return this.authenticateShein();
            }
            if (this.channel.isShopify()) {
                return this.openShopifyModal();
            }
            if (this.channel.isMagazineLuiza()) {
                return this.authenticateMagazineLuiza();
            }
            if (this.channel.isTiktokShop()) {
                return this.authenticateTiktokShop();
            }
            return this.authenticateLegacy();
        },

        openShopifyModal() {
            this.shopifyShopName = this.credentials.shop || ''
            ModalService.show('#shopify-shop-modal')
        },

        closeShopifyModal() {
            ModalService.hide('#shopify-shop-modal')
        },

        async confirmShopifyShop() {
            if (!this.shopifyShopName) {
                return
            }
            ModalService.hide('#shopify-shop-modal')
            this.credentials.setShop(this.shopifyShopName)
            return this.authenticateShopify()
        },

        async authenticateShopify() {
            this.authenticating = true;
            const floatingAlertObj = this.$floatingAlert;
            let finished = false;

            try {
                const MAX_WAIT_TIME = 10 * 60 * 1000;
                const POLL_INTERVAL = 1000;

                const shop = this.credentials.shop;
                const { url } = await channelService.getAuthorizationUrl(this.channel.enumChannel, shop);

                window.open(url, '_blank', 'resizable,scrollbars=yes,status,width=800,height=600');

                const cleanup = () => {
                    if (finished) { return; }
                    finished = true;
                    clearTimeout(timeoutId);
                    clearInterval(pollId);
                };

                // console.log('[Shopify Auth] iniciando polling para shop:', shop);

                const pollId = setInterval(async () => {
                    if (finished) { return; }
                    try {
                        const result = await channelService.pollShopifyAuth(shop);
                        // console.log('[Shopify Auth] poll resposta:', result);
                        if (!result.ready) { return; }

                        cleanup();
                        this.credentials.setCode(result.code);

                        try {
                            this.credentials.shop = shop;
                            const tokens = await channelService.saveCredentials(this.channel.id, this.credentials);
                            // const tokens = await channelService.getAuthenticationTokens(this.channel.id, this.credentials);
                            const tokensWithInfo = {
                                ...tokens,
                                idChannel: this.channel.id,
                                subscriberPublicKey: subscriberManager.getPublicKeyFromStorage(),
                            };
                            this.credentials.updateTokens(tokensWithInfo);
                            // await channelService.saveCredentials(this.channel.id, this.credentials);

                            this.authenticating = false;
                            floatingAlertObj.success({ message: 'Conexão com o Shopify realizada com sucesso!' });
                        } catch (err) {
                            this.authenticating = false;
                            floatingAlertObj.error({
                                message: err.message || 'Houve um problema no vínculo Muven x Shopify.',
                            });
                        }
                    } catch (err) {
                        // console.error('[Shopify Auth] poll ERRO:', err && err.response && err.response.status, err && err.message);
                    }
                }, POLL_INTERVAL);

                const timeoutId = setTimeout(() => {
                    cleanup();
                    this.authenticating = false;
                    floatingAlertObj.error({ message: 'Tempo limite atingido para autenticar com o Shopify.' });
                }, MAX_WAIT_TIME);

            } catch (err) {
                this.authenticating = false;
                floatingAlertObj.error({
                    message: err.message || 'Houve um problema no momento de fazer o vínculo Muven x Shopify.',
                });
            }
        },

        async authenticateLegacy() {
            this.authenticating = true;
            this.authenticating = true;

            try {
                const TIME_CHECK_IF_CODE_READY = 2000
                const shop = this.channel.isShopify() ? this.credentials.shop : undefined
                const { url } = await channelService.getAuthorizationUrl(this.channel.enumChannel, shop)
                const win = window.open(url, "_blank", "resizable,scrollbars,status,width=800,height=600")

                const idInterval = setInterval(async () => {
                    if (!/\?code/.test(win.location.search)) {
                        return
                    }

                    if (win.closed) {
                        this.authenticating = false
                        clearInterval(idInterval)
                        return
                    }

                    clearInterval(idInterval);

                    const urlParams = new URLSearchParams(win.location.search)
                    const code = urlParams.get("code")
                    this.credentials.setCode(code)

                    if (this.channel.isShopify()) {
                        const shopRaw = urlParams.get("shop")
                        const shopFromCallback = shopRaw ? shopRaw.replace('.myshopify.com', '') : ''
                        if (shopFromCallback) {
                            this.credentials.setShop(shopFromCallback)
                        }
                        win.close()
                    } else if (this.channel.isShopee()) {
                        const shopId = urlParams.get("shop_id")
                        this.credentials.setShopId(shopId)
                        win.close()
                    } else {
                        win.close()
                    }

                    try {
                        await channelService.saveCredentials(this.channel.id, this.credentials)

                        const tokens = await channelService.getAuthenticationTokens(this.channel.id, this.credentials);
                        const tokensWithAggregatedInfo = {
                            ...tokens,
                            idChannel: this.channel.id,
                            subscriberPublicKey: subscriberManager.getPublicKeyFromStorage(),
                        }

                        this.credentials.updateTokens(tokensWithAggregatedInfo);
                        const isNuvemShop = this.channel.isNuvemShop();

                        if (!isNuvemShop) {
                            await channelService.saveCredentials(this.channel.id, this.credentials);
                        } else {
                            this.$floatingAlert.success({ message: 'Conexão com o canal Nuvemshop feita com sucesso!' });
                            setTimeout(() => {
                                window.location.reload();
                            }, 1500);
                        }

                        if (this.channel.isMercadoLivre()) {
                            const sellerInfo = await channelService.getSellerInfo(this.channel.id, this.credentials)
                            this.credentials.setSellerId(sellerInfo)
                            await channelService.saveCredentials(this.channel.id, this.credentials)
                        }
                    } catch (err) {
                        this.$floatingAlert.error({
                            message: err.message || `Houve um problema no momento de fazer o vínculo Muven x ${this.channel.name}.`,
                        })
                    }

                    this.authenticating = false
                }, TIME_CHECK_IF_CODE_READY)
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message || `Houve um problema no momento de fazer o vínculo Muven x ${this.channel.name}.`,
                })

                this.authenticating = false
            }
        },

        async authenticateShein() {
            this.authenticating = true;
            const floatingAlertObj = this.$floatingAlert;

            let finished = false;

            try {
                const MAX_WAIT_TIME = 10 * 60 * 1000;
                const CHECK_INTERVAL = 500;

                const { url } = await channelService.getAuthorizationUrl(this.channel.enumChannel);

                const win = window.open(
                    url,
                    "_blank",
                    "resizable,scrollbars=yes,status,width=1200,height=800"
                );

                if (!win) {
                    throw new Error("Não foi possível abrir a janela de autenticação da SHEIN.");
                }

                const messageHandler = async (event) => {

                    if (event.data.type === 'SHEIN_AUTH_SUCCESS') {

                        cleanup();

                        if (win && !win.closed) {
                            win.close();
                        }

                        this.credentials.tempToken = event.data.tempToken;

                        this.credentials.setCode(event.data.tempToken);
                        const resp = await channelService.saveCredentials(this.channel.id, this.credentials);
                        this.credentials.setCredentials(resp.openKeyId, resp.secretKey);
                        this.credentials.setAuthorizationCompleted(true);
                        this.authenticating = false;
                        floatingAlertObj.success({
                            message: "Autorização com a SHEIN realizada com sucesso!",
                        });
                    }

                    if (event.data.type === 'SHEIN_AUTH_ERROR') {
                        cleanup();
                        this.authenticating = false;
                        floatingAlertObj.error({
                            message: "Erro na autenticação: " + (event.data.message || "Token não encontrado"),
                        });
                    }
                };

                // Adiciona o listener
                window.addEventListener('message', messageHandler);
                const cleanup = () => {
                    if (finished) {
                        return;
                    }
                    finished = true;

                    clearTimeout(timeoutId);
                    clearInterval(pollingId);
                    window.removeEventListener('message', messageHandler);
                    // this.authenticating = false;
                };

                const timeoutId = setTimeout(() => {
                    cleanup();

                    if (win && !win.closed) {
                        win.close();
                    }

                    this.authenticating = false;
                    floatingAlertObj.error({
                        message: "Tempo limite atingido para autenticar com o canal SHEIN.",
                    });
                }, MAX_WAIT_TIME);

                // 2. Use polling apenas para verificar se a janela foi fechada manualmente
                const pollingId = setInterval(() => {
                    if (!win || win.closed) {
                        cleanup();

                        // Envia uma mensagem para si mesmo indicando que foi fechado
                        // Isso garante que o handler será executado mesmo após cleanup
                        if (!finished) {
                            this.authenticating = false;
                            floatingAlertObj.error({
                                message: "Autenticação com a SHEIN foi cancelada pelo usuário.",
                            });
                        }
                        return;
                    }
                }, CHECK_INTERVAL);

            } catch (err) {
                this.authenticating = false;

                floatingAlertObj.error({
                    message:
                        err.message ||
                        `Houve um problema no momento de fazer o vínculo Muven x ${this.channel.name}.`,
                });
            }
        },

        async authenticateMagazineLuiza() {
            this.authenticating = true;
            const floatingAlertObj = this.$floatingAlert;
            let finished = false;

            try {
                const MAX_WAIT_TIME = 10 * 60 * 1000;
                const CHECK_INTERVAL = 500;

                const { url } = await channelService.getAuthorizationUrl(this.channel.enumChannel);

                const win = window.open(
                    url,
                    "_blank",
                    "resizable,scrollbars=yes,status,width=800,height=600"
                );

                if (!win) {
                    throw new Error("Não foi possível abrir a janela de autenticação do Magazine Luíza.");
                }

                const messageHandler = async (event) => {
                    let code = null;
                    let state = null;

                    if (event.data && event.data.type === 'MAGAZINELUIZA_AUTH_SUCCESS') {
                        code = event.data.code;
                        state = event.data.state || '';
                    } else if (event.origin === 'https://seller.magalu.com' && event.data && event.data.code) {
                        code = event.data.code;
                        state = event.data.state || '';
                    }

                    if (code && !finished) {
                        cleanup();
                        if (win && !win.closed) { win.close(); }
                        try {
                            this.credentials.setCode(code);
                            this.credentials.setState(state);
                            await channelService.saveCredentials(this.channel.id, this.credentials);
                            const tokens = await channelService.getAuthenticationTokens(this.channel.id, this.credentials);
                            const tokensWithAggregatedInfo = {
                                ...tokens,
                                idChannel: this.channel.id,
                                subscriberPublicKey: subscriberManager.getPublicKeyFromStorage(),
                            };
                            this.credentials.updateTokens(tokensWithAggregatedInfo);
                            await channelService.saveCredentials(this.channel.id, this.credentials);
                            this.authenticating = false;
                            floatingAlertObj.success({ message: 'Conexão com Magazine Luíza realizada com sucesso!' });
                        } catch (err) {
                            this.authenticating = false;
                            floatingAlertObj.error({
                                message: err.message || 'Houve um problema no momento de fazer o vínculo Muven x Magazine Luíza.',
                            });
                        }
                    }

                    if (event.data && event.data.type === 'MAGAZINELUIZA_AUTH_ERROR') {
                        cleanup();
                        this.authenticating = false;
                        floatingAlertObj.error({
                            message: 'Erro na autenticação: ' + (event.data.message || 'Código não encontrado'),
                        });
                    }
                };

                window.addEventListener('message', messageHandler);

                const cleanup = () => {
                    if (finished) { return; }
                    finished = true;
                    clearTimeout(timeoutId);
                    clearInterval(pollingId);
                    window.removeEventListener('message', messageHandler);
                };

                const timeoutId = setTimeout(() => {
                    cleanup();
                    if (win && !win.closed) { win.close(); }
                    this.authenticating = false;
                    floatingAlertObj.error({ message: 'Tempo limite atingido para autenticar com o Magazine Luíza.' });
                }, MAX_WAIT_TIME);

                const pollingId = setInterval(async () => {
                    if (!win || win.closed) {
                        if (!finished) {
                            cleanup();
                            this.authenticating = false;
                            floatingAlertObj.error({ message: 'Autenticação com o Magazine Luíza foi cancelada.' });
                        }
                        return;
                    }

                    // Tenta ler URL do popup (só funciona quando mesma origem; SecurityError é silenciado enquanto cross-origin)
                    try {
                        const href = win.location.href;
                        if (href.includes('/authorization_code_for/magazine-luiza')) {
                            const params = new URLSearchParams(win.location.search);
                            const code = params.get('code');
                            const state = params.get('state') || '';
                            if (code && !finished) {
                                cleanup();
                                win.close();
                                try {
                                    this.credentials.setCode(code);
                                    this.credentials.setState(state);
                                    await channelService.saveCredentials(this.channel.id, this.credentials);
                                    const tokens = await channelService.getAuthenticationTokens(this.channel.id, this.credentials);
                                    const tokensWithAggregatedInfo = {
                                        ...tokens,
                                        idChannel: this.channel.id,
                                        subscriberPublicKey: subscriberManager.getPublicKeyFromStorage(),
                                    };
                                    this.credentials.updateTokens(tokensWithAggregatedInfo);
                                    await channelService.saveCredentials(this.channel.id, this.credentials);
                                    this.authenticating = false;
                                    floatingAlertObj.success({ message: 'Conexão com Magazine Luíza realizada com sucesso!' });
                                } catch (err) {
                                    this.authenticating = false;
                                    floatingAlertObj.error({
                                        message: err.message || 'Houve um problema no momento de fazer o vínculo Muven x Magazine Luíza.',
                                    });
                                }
                            }
                        }
                    } catch (_) {
                        // Popup ainda em domínio cross-origin (Magalu), ignorar
                    }
                }, CHECK_INTERVAL);

            } catch (err) {
                this.authenticating = false;
                floatingAlertObj.error({
                    message: err.message || `Houve um problema no momento de fazer o vínculo Muven x ${this.channel.name}.`,
                });
            }
        },

        async authenticateTiktokShop() {
            this.authenticating = true;
            const floatingAlertObj = this.$floatingAlert;
            let finished = false;

            try {
                const MAX_WAIT_TIME = 10 * 60 * 1000;
                const CHECK_INTERVAL = 1000;
                // Com Cross-Origin-Opener-Policy o popup pode parecer fechado logo ao abrir o TikTok
                const CLOSED_GRACE_TIME = 2 * 60 * 1000;

                const { url } = await channelService.getAuthorizationUrl(this.channel.enumChannel);

                const win = window.open(
                    url,
                    "_blank",
                    "resizable,scrollbars=yes,status,width=800,height=600"
                );

                if (!win) {
                    throw new Error("Não foi possível abrir a janela de autenticação do TikTok Shop.");
                }

                const finishWithCode = async (code, state, shopRegion) => {
                    cleanup();
                    if (win && !win.closed) { win.close(); }
                    try {
                        this.credentials.setCode(code);
                        this.credentials.setState(state || '');
                        this.credentials.setShopRegion(shopRegion || '');
                        const resp = await channelService.saveCredentials(this.channel.id, this.credentials);
                        this.credentials.setFromResponse(resp);
                        this.authenticating = false;
                        floatingAlertObj.success({ message: 'Conexão com o TikTok Shop realizada com sucesso!' });
                    } catch (err) {
                        this.authenticating = false;
                        floatingAlertObj.error({
                            message: err.message || 'Houve um problema no momento de fazer o vínculo Muven x TikTok Shop.',
                        });
                    }
                };

                const messageHandler = async (event) => {
                    if (finished || !event.data) { return; }

                    if (event.data.type === 'TIKTOK_SHOP_AUTH_SUCCESS' && event.data.code) {
                        await finishWithCode(event.data.code, event.data.state, event.data.shopRegion);
                    }

                    if (event.data.type === 'TIKTOK_SHOP_AUTH_ERROR') {
                        cleanup();
                        if (win && !win.closed) { win.close(); }
                        this.authenticating = false;
                        floatingAlertObj.error({
                            message: 'Erro na autenticação: ' + (event.data.message || 'Código não encontrado'),
                        });
                    }
                };

                window.addEventListener('message', messageHandler);

                const cleanup = () => {
                    if (finished) { return; }
                    finished = true;
                    clearTimeout(timeoutId);
                    clearInterval(pollingId);
                    window.removeEventListener('message', messageHandler);
                };

                const timeoutId = setTimeout(() => {
                    cleanup();
                    if (win && !win.closed) { win.close(); }
                    this.authenticating = false;
                    floatingAlertObj.error({ message: 'Tempo limite atingido para autenticar com o TikTok Shop.' });
                }, MAX_WAIT_TIME);

                const state = subscriberManager.getPublicKeyFromStorage();
                let closedAt = 0;

                const pollingId = setInterval(async () => {
                    if (finished) { return; }

                    // O callback grava o code no Redis; funciona mesmo cross-origin ou com o opener cortado (COOP)
                    try {
                        const result = await channelService.pollTiktokShopAuth(state);
                        if (result.ready && result.code && !finished) {
                            await finishWithCode(result.code, result.state, result.shopRegion);
                            return;
                        }
                    } catch (_) {
                        // Falha pontual no polling, tenta de novo no próximo ciclo
                    }

                    // Fallback: lê a URL do callback quando o popup está na mesma origem
                    try {
                        if (win && !win.closed && win.location.href.includes('/authorization_code_for/tiktok-shop')) {
                            const params = new URLSearchParams(win.location.search);
                            const code = params.get('code');
                            if (code && !finished) {
                                await finishWithCode(code, params.get('state'), params.get('shop_region'));
                                return;
                            }
                        }
                    } catch (_) {
                        // Popup ainda em domínio cross-origin (TikTok), ignorar
                    }

                    // Popup fechado: aguarda um pouco o code chegar antes de considerar cancelado
                    if (!finished && (!win || win.closed)) {
                        closedAt = closedAt || Date.now();
                        if (Date.now() - closedAt > CLOSED_GRACE_TIME) {
                            cleanup();
                            this.authenticating = false;
                            floatingAlertObj.error({ message: 'Autenticação com o TikTok Shop foi cancelada.' });
                        }
                    }
                }, CHECK_INTERVAL);

            } catch (err) {
                this.authenticating = false;
                floatingAlertObj.error({
                    message: err.message || `Houve um problema no momento de fazer o vínculo Muven x ${this.channel.name}.`,
                });
            }
        },

        async removeLink() {
            this.removingLink = true

            try {
                await channelService.saveCredentials(this.channel.id, {})
                this.credentials.reset()
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.removingLink = false
        },
        async testConnection() {
            this.testingConnection = true;

            try {
                await channelService.pingChannel(this.channel.enumChannel, this.credentials, this.channel.id)
                this.lastTest = { ok: true, message: '', at: this.formatNow() }
                this.$floatingAlert.success({
                    message: 'Conexão com o canal feita com sucesso!',
                })
            } catch (e) {
                this.lastTest = { ok: false, message: e.message || 'o canal não respondeu.', at: this.formatNow() }
                this.$floatingAlert.error({
                    message: e.message || 'Não foi possível abrir uma conexão com o canal. Caso necessário, desfaça a integração e tente novamente.',
                })
            }

            this.testingConnection = false
        },
        // Desconectar é perigoso: só depois de digitar a palavra de confirmação
        askRemoveLink() {
            this.$refs.confirmRemoveLink.show()
        },
        async confirmRemoveLink() {
            await this.removeLink()

            if (!this.credentials.isComplete()) {
                this.lastTest = null
            }
        },
        backToSettingsList() {
            this.$router.push(`/canais/${this.channel.id}/configuracoes`)
        },
        formatNow() {
            return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        },
    },
    computed: {
        brand() {
            return getChannelBrand(this.channel.enumChannel)
        },
        channelMarkUrl() {
            return this.brand.mark ? require(`@/channel/images/marks/${this.brand.mark}`) : null
        },
        muvenMarkUrl() {
            return require('@/channel/images/marks/muven-cart.png')
        },
        // Só lê os flags da autenticação; quem muda o estado continuam sendo os métodos de cada canal
        connectionState() {
            if (this.credentials.isComplete()) {
                return 'connected'
            }

            return this.authenticating ? 'connecting' : 'idle'
        },
        connectionStateLabel() {
            return {
                connected: 'Conectado',
                connecting: 'Conectando',
                idle: 'Não conectado',
            }[this.connectionState]
        },
        steps() {
            const name = this.channel.getRealName()

            return [
                {
                    title: 'Clique em Conectar',
                    description: `Abrimos uma janela do ${name}.`,
                },
                {
                    title: `Entre na sua conta ${name} e autorize o Muven`,
                    description: this.authenticating
                        ? `Aguardando você concluir na janela do ${name}.`
                        : 'Use o login de quem administra a loja no canal.',
                },
                {
                    title: 'Pronto',
                    description: 'Voltamos para esta tela sozinhos, já conectados.',
                },
            ]
        },
        currentStep() {
            return this.authenticating ? 1 : 0
        },
    },
}
</script>

<style lang="scss" scoped>
/* Mesma regra das outras telas do canal: neutro por padrão, roxo na ação,
   verde/amarelo/vermelho só para estado. */

$ease-out: cubic-bezier(.22, 1, .36, 1);
$display: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;

.channel-auth {
    max-width: 860px;
    margin: 0 auto;
    animation: ca-rise .5s $ease-out both;
}

/* ── Selo de estado (no cabeçalho) ── */

.ca-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 14px 5px 12px;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--surface-2);
    color: var(--text-muted);
    font-weight: 700;

    &.is-connected {
        border-color: var(--success-border);
        background: var(--success-bg);
        color: var(--success-fg);
    }

    &.is-connecting {
        border-color: var(--tint-brand-border);
        background: var(--tint-brand-bg);
        color: var(--brand);

        .ca-status-dot {
            animation: ca-pulse 1.2s ease-in-out infinite;
        }
    }
}

.ca-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
}

/* ── Painel ── */

.ca-panel {
    margin-top: 16px;
    padding: 32px 36px;
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    background: var(--surface-1);
}

.ca-title {
    margin: 28px 0 0;
    color: var(--text-strong);
    font-family: $display;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -.4px;
    text-align: center;
}

.ca-lead {
    max-width: 520px;
    margin: 6px auto 0;
    color: var(--text-muted);
    font-size: 15px;
    text-align: center;
}

/* ── Passo a passo ── */

.ca-steps {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 520px;
    margin: 24px auto 0;
    padding: 0;
    list-style: none;
}

.ca-step {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 12px 14px;
    border: 1px solid transparent;
    border-radius: 12px;
    transition: background-color .2s, border-color .2s;

    &.is-current {
        border-color: var(--tint-brand-border);
        background: var(--tint-brand-soft);

        .ca-step-marker {
            border-color: var(--brand);
            background: var(--brand-fill);
            color: var(--text-on-brand);
        }
    }

    &.is-done .ca-step-marker {
        border-color: var(--border);
        color: var(--text-soft);
    }
}

.ca-step-marker {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 1px solid var(--border);
    border-radius: 50%;
    background: var(--surface-2);
    color: var(--text-muted);
    font-size: 13px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.ca-step-text {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding-top: 2px;

    strong {
        color: var(--text-strong);
    }

    span {
        color: var(--text-muted);
        font-size: 14px;
    }
}

.ca-cta {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: 24px;
}

.ca-hint {
    margin: 0;
    color: var(--text-faint);
    font-size: 13px;
}

.ca-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    // O override global do btn-primary não cobre o desabilitado: sem isto vira azul
    &.btn-primary:disabled {
        border-color: var(--brand-solid);
        background-color: var(--brand-solid);
        opacity: .65;
    }
}

/* ── Notas ── */

.ca-note {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    max-width: 520px;
    margin: 20px auto 0;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.45;

    &.is-warning {
        padding: 10px 12px;
        border: 1px solid var(--warning-border);
        border-radius: 10px;
        background: var(--warning-bg);
        color: var(--warning-fg);
    }
}

.ca-note-icon {
    flex-shrink: 0;
    margin-top: 3px;
}

/* ── Conectado: saúde e zona de risco ── */

.ca-health,
.ca-danger {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 20px;
}

.ca-health {
    margin-top: 28px;
    padding: 16px 18px;
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    background: var(--surface-2);
}

.ca-health-text,
.ca-danger-text {
    display: flex;
    flex: 1 1 280px;
    flex-direction: column;
    gap: 3px;
    min-width: 0;

    strong {
        color: var(--text-strong);
    }
}

.ca-test-result {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: var(--text-muted);
    font-size: 14px;

    svg {
        flex-shrink: 0;
        margin-top: 3px;
    }

    small {
        display: block;
        margin-top: 2px;
        opacity: .85;
    }

    &.is-ok {
        color: var(--success-fg);
        font-weight: 600;
    }

    &.is-error {
        color: var(--danger-fg);
        font-weight: 600;
    }
}

.ca-danger {
    margin-top: 28px;
    padding-top: 22px;
    border-top: 1px solid var(--border-subtle);
}

.ca-danger-text span {
    color: var(--text-muted);
    font-size: 14px;
}

/* ── Rodapé ── */

.ca-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    padding: 10px;
    border: 1px solid var(--border-subtle);
    border-radius: 14px;
    background: var(--surface-1);
    box-shadow: var(--shadow-sm);

    .ca-button {
        min-width: 110px;
    }
}

@keyframes ca-rise {
    from { opacity: 0; translate: 0 10px; }
    to   { opacity: 1; translate: 0 0; }
}

@keyframes ca-pulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: .3; }
}

@media (max-width: 576px) {
    .ca-panel {
        padding: 24px 16px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .channel-auth,
    .ca-status-dot {
        animation: none !important;
    }
}
</style>
